/**
 * Hexagonal Coordinate System
 * Based on spec §3 and §4
 *
 * NORMATIVE: (q,r) are the canonical coordinates
 * INFORMATIVE: (x,y) are for visualization only
 */

import type { HexCoord, CartesianCoord } from '@/types/hex';

// Spec §3.3: Module spacing (NORMATIVE)
// Minimum spacing so no two modules share a (q,r) pair
export const MODULE_SPACING_Q = 4; // Width in q direction (16-key default)
export const MODULE_SPACING_R = 4; // Height in r direction (matches LAYOUT_SPACING_Y)

// Fixed q-shift per module row to maintain continuous hex grid across module boundaries
export const MODULE_Q_SHIFT = -2;

// Configurable stagger: additional coordinate offset per axis step (default: none)
export const DEFAULT_STAGGER = { q: 0, r: 0 };

/**
 * Convert module position + local key coordinate to global (q,r)
 * Spec §3.3: Global coordinate calculation (NORMATIVE)
 *
 * @param moduleCol - Module column (0-7)
 * @param moduleRow - Module row (0-3)
 * @param localQ - Local q coordinate within module
 * @param localR - Local r coordinate within module
 * @returns Global hexagonal coordinate
 */
export function localToGlobal(
  moduleCol: number,
  moduleRow: number,
  localQ: number,
  localR: number,
  stagger: { q: number; r: number } = DEFAULT_STAGGER,
  moduleRowShift: number = 0
): HexCoord {
  // Fixed -2 compensation + configurable additional shift
  const totalShift = MODULE_Q_SHIFT + moduleRowShift;
  const baseQ = moduleCol * MODULE_SPACING_Q + localQ + moduleRow * totalShift;
  const baseR = moduleRow * MODULE_SPACING_R + localR;
  return {
    q: baseQ + baseR * stagger.q,
    r: baseR + baseQ * stagger.r,
  };
}

/**
 * Convert global (q,r) to module position and local coordinate
 * Inverse of localToGlobal
 *
 * @param coord - Global hexagonal coordinate
 * @returns Module position and local coordinate
 */
export function globalToLocal(
  coord: HexCoord,
  stagger: { q: number; r: number } = DEFAULT_STAGGER,
  moduleRowShift: number = 0
): {
  moduleCol: number;
  moduleRow: number;
  localQ: number;
  localR: number;
} {
  // Invert the stagger transform: solve for baseQ, baseR from globalQ, globalR
  // globalQ = baseQ + baseR * stagger.q
  // globalR = baseR + baseQ * stagger.r
  const denom = 1 - stagger.q * stagger.r;
  if (denom === 0) {
    throw new Error('Invalid stagger configuration: q * r === 1 makes the transform non-invertible');
  }
  const baseQ = Math.round((coord.q - coord.r * stagger.q) / denom);
  const baseR = Math.round((coord.r - coord.q * stagger.r) / denom);

  // Fixed -2 compensation + configurable additional shift
  const totalShift = MODULE_Q_SHIFT + moduleRowShift;
  const moduleRow = Math.floor(baseR / MODULE_SPACING_R);
  const localR = baseR - moduleRow * MODULE_SPACING_R;

  const qWithoutModuleShift = baseQ - moduleRow * totalShift;
  const moduleCol = Math.floor(qWithoutModuleShift / MODULE_SPACING_Q);
  const localQ = qWithoutModuleShift - moduleCol * MODULE_SPACING_Q;

  return { moduleCol, moduleRow, localQ, localR };
}

/**
 * Convert hexagonal (q,r) to cartesian (x,y) for rendering
 * Spec §4.2: Conversion formula (INFORMATIVE)
 *
 * Formula: x = q, y = r + floor(q / 2)
 * For flat-top hexagons
 *
 * @param coord - Hexagonal coordinate
 * @returns Cartesian coordinate for visualization
 */
export function hexToCartesian(coord: HexCoord): CartesianCoord {
  return {
    x: coord.q,
    y: coord.r + Math.floor(coord.q / 2),
  };
}

/**
 * Convert hexagonal (q,r) to pixel position for SVG rendering
 * Spec §3.3: Proper flat-top hexagon geometry with MODULE_SPACING_Q/R = 4
 *
 * @param coord - Hexagonal coordinate
 * @param size - Hexagon size (distance from center to corner)
 * @returns Pixel position {x, y}
 */
export function hexToPixel(
  coord: HexCoord,
  size: number
): { x: number; y: number } {
  const SQRT3 = Math.sqrt(3);

  // flat-top hex axial → pixel
  const x = size * (3 / 2) * coord.q;
  const y = size * SQRT3 * (coord.r + coord.q / 2);

  return { x, y };
}

/**
 * Calculate hexagonal distance between two coordinates
 * Spec §4.1: Distance calculation
 *
 * @param a - First coordinate
 * @param b - Second coordinate
 * @returns Integer distance in hexagonal grid steps
 */
export function hexDistance(a: HexCoord, b: HexCoord): number {
  const s1 = -a.q - a.r; // Third axial coordinate
  const s2 = -b.q - b.r;
  return Math.max(
    Math.abs(a.q - b.q),
    Math.abs(a.r - b.r),
    Math.abs(s1 - s2)
  );
}

/**
 * Get all 6 neighbors of a hexagonal coordinate
 * Spec §4.1: Natural neighborhood in 6 directions
 *
 * @param coord - Center coordinate
 * @returns Array of 6 neighboring coordinates
 */
export function hexNeighbors(coord: HexCoord): HexCoord[] {
  // 6 directions for flat-top hexagons
  const directions: HexCoord[] = [
    { q: 1, r: 0 },   // East
    { q: 1, r: -1 },  // Northeast
    { q: 0, r: -1 },  // Northwest
    { q: -1, r: 0 },  // West
    { q: -1, r: 1 },  // Southwest
    { q: 0, r: 1 },   // Southeast
  ];

  return directions.map((dir) => ({
    q: coord.q + dir.q,
    r: coord.r + dir.r,
  }));
}

/**
 * Check if two hexagonal coordinates are equal
 *
 * @param a - First coordinate
 * @param b - Second coordinate
 * @returns true if coordinates are equal
 */
export function hexEquals(a: HexCoord, b: HexCoord): boolean {
  return a.q === b.q && a.r === b.r;
}

/**
 * Convert hexagonal coordinate to string key for Maps/Sets
 * Useful for lookups and deduplication
 *
 * @param coord - Hexagonal coordinate
 * @returns String key in format "q,r"
 */
export function hexToKey(coord: HexCoord): string {
  return `${coord.q},${coord.r}`;
}

/**
 * Parse hex coordinate from string key
 *
 * @param key - String key in format "q,r"
 * @returns Hexagonal coordinate or null if invalid
 */
export function keyToHex(key: string): HexCoord | null {
  const parts = key.split(',');
  if (parts.length !== 2) return null;

  const q = parseInt(parts[0] ?? '', 10);
  const r = parseInt(parts[1] ?? '', 10);

  if (isNaN(q) || isNaN(r)) return null;

  return { q, r };
}

/**
 * Key definition with both canonical (q,r) and layout (x,y) coordinates
 * Spec §3.1 and §3.2
 */
export interface KeyCoordinates {
  hex: HexCoord; // Canonical (q,r) - NORMATIVE
  layout: CartesianCoord; // Layout (x,y) - INFORMATIVE, for visualization only
}

/**
 * Module key definitions - proper axial hex coordinates
 * NORMATIVE: (q,r) coordinates - even-r offset → axial conversion
 * INFORMATIVE: (x,y) coordinates - physical layout on PCB
 *
 * Physical layout: 4 rows, 3 keys per row, alternating stagger
 * Row numbering: bottom-up (y=1 at bottom, y=4 at top)
 * Key numbering: left-to-right, row-by-row from bottom
 *
 * Axial coordinates ensure all 6 physical neighbor directions have
 * constant (Δq, Δr), which is required for isomorphic note mapping:
 *   Same row right:  (+1,  0)    Same row left:  (-1,  0)
 *   Upper-right:     ( 0, +1)    Lower-left:     ( 0, -1)
 *   Upper-left:      (-1, +1)    Lower-right:    (+1, -1)
 */
const MODULE_KEYS_12: KeyCoordinates[] = [
  { hex: { q: 0, r: 1 }, layout: { x: 2, y: 1 } },   // Key 0  - row 1, left
  { hex: { q: 1, r: 1 }, layout: { x: 4, y: 1 } },   // Key 1  - row 1, middle
  { hex: { q: 2, r: 1 }, layout: { x: 6, y: 1 } },   // Key 2  - row 1, right
  { hex: { q: -1, r: 2 }, layout: { x: 1, y: 2 } },  // Key 3  - row 2, left
  { hex: { q: 0, r: 2 }, layout: { x: 3, y: 2 } },   // Key 4  - row 2, middle
  { hex: { q: 1, r: 2 }, layout: { x: 5, y: 2 } },   // Key 5  - row 2, right
  { hex: { q: -1, r: 3 }, layout: { x: 2, y: 3 } },  // Key 6  - row 3, left
  { hex: { q: 0, r: 3 }, layout: { x: 4, y: 3 } },   // Key 7  - row 3, middle
  { hex: { q: 1, r: 3 }, layout: { x: 6, y: 3 } },   // Key 8  - row 3, right
  { hex: { q: -2, r: 4 }, layout: { x: 1, y: 4 } },  // Key 9  - row 4, left
  { hex: { q: -1, r: 4 }, layout: { x: 3, y: 4 } },  // Key 10 - row 4, middle
  { hex: { q: 0, r: 4 }, layout: { x: 5, y: 4 } },   // Key 11 - row 4, right
];

const MODULE_KEYS_16: KeyCoordinates[] = [
  { hex: { q: 0, r: 1 }, layout: { x: 2, y: 1 } },   // Key 0
  { hex: { q: 1, r: 1 }, layout: { x: 4, y: 1 } },   // Key 1
  { hex: { q: 2, r: 1 }, layout: { x: 6, y: 1 } },   // Key 2
  { hex: { q: 3, r: 1 }, layout: { x: 8, y: 1 } },   // Key 3
  { hex: { q: -1, r: 2 }, layout: { x: 1, y: 2 } },  // Key 4
  { hex: { q: 0, r: 2 }, layout: { x: 3, y: 2 } },   // Key 5
  { hex: { q: 1, r: 2 }, layout: { x: 5, y: 2 } },   // Key 6
  { hex: { q: 2, r: 2 }, layout: { x: 7, y: 2 } },   // Key 7
  { hex: { q: -1, r: 3 }, layout: { x: 2, y: 3 } },  // Key 8
  { hex: { q: 0, r: 3 }, layout: { x: 4, y: 3 } },   // Key 9
  { hex: { q: 1, r: 3 }, layout: { x: 6, y: 3 } },   // Key 10
  { hex: { q: 2, r: 3 }, layout: { x: 8, y: 3 } },   // Key 11
  { hex: { q: -2, r: 4 }, layout: { x: 1, y: 4 } },  // Key 12
  { hex: { q: -1, r: 4 }, layout: { x: 3, y: 4 } },  // Key 13
  { hex: { q: 0, r: 4 }, layout: { x: 5, y: 4 } },   // Key 14
  { hex: { q: 1, r: 4 }, layout: { x: 7, y: 4 } },   // Key 15
];

/**
 * Get module key definitions for the given module type
 */
export function getModuleKeys(keysPerModule: 12 | 16): KeyCoordinates[] {
  return keysPerModule === 16 ? MODULE_KEYS_16 : MODULE_KEYS_12;
}

/**
 * Get module spacing in Q direction for the given module type
 */
export function getModuleSpacingQ(keysPerModule: 12 | 16): number {
  return keysPerModule === 16 ? 4 : 3;
}

/**
 * Get layout spacing in X direction for the given module type
 */
export function getLayoutSpacingX(keysPerModule: 12 | 16): number {
  return keysPerModule === 16 ? 8 : 6;
}

/**
 * Convert layout (x,y) to pixel position for SVG rendering
 * Spec §3.2: Layout coordinates are for visualization only
 *
 * Note: Layout coordinates match SVG convention:
 * - y=1 at bottom (small SVG y)
 * - y=maxY at top (large SVG y)
 * - SVG y grows downward, so we invert to show y=1 at visual bottom
 *
 * @param layout - Layout coordinate (x,y)
 * @param size - Hexagon size (distance from center to corner)
 * @param maxY - Maximum y value for the grid (for single module: 4, for multi-module: calculated from rows)
 * @returns Pixel position {x, y}
 */
export function layoutToPixel(
  layout: CartesianCoord,
  size: number,
  maxY: number = 4
): { x: number; y: number } {
  const SQRT3 = Math.sqrt(3);

  // Flat-top hexagon geometry:
  // - Width (side to side, horizontal) = sqrt(3) * size
  // - Height (corner to corner, vertical) = 2 * size
  //
  // In offset layout, rows are staggered so hexagons touch:
  // - Horizontal spacing per unit x (Δx=2): sqrt(3)*size / 2
  // - Vertical spacing per unit y (Δy=1): 3/4 of height = 3/4 * 2*size = 1.5*size
  const HEX_H_SPACING = (SQRT3 / 2) * size; // Horizontal: sqrt(3)*size / 2 for Δx=2
  const HEX_V_SPACING = 1.5 * size; // Vertical: 1.5*size for offset layout

  // Invert Y: y=1 should be at bottom (larger SVG y), y=maxY at top (smaller SVG y)
  return {
    x: layout.x * HEX_H_SPACING,
    y: (maxY - layout.y + 1) * HEX_V_SPACING, // Invert: y=1→bottom, y=maxY→top
  };
}

/**
 * Generate all key coordinates for a module grid
 * Spec §3.3: Module spacing and global coordinates
 *
 * @param columns - Number of module columns (1-8, horizontal)
 * @param rows - Number of module rows (1-4, vertical)
 * @returns Array of all key coordinates with both hex and layout coordinates
 */
export function generateAllCoords(
  columns: number,
  rows: number,
  keysPerModule: 12 | 16 = 16,
  stagger: { q: number; r: number } = DEFAULT_STAGGER,
  moduleRowShift: number = 0
): KeyCoordinates[] {
  const allCoords: KeyCoordinates[] = [];

  const moduleKeys = getModuleKeys(keysPerModule);
  const spacingQ = getModuleSpacingQ(keysPerModule);
  const LAYOUT_SPACING_X = getLayoutSpacingX(keysPerModule);
  const LAYOUT_SPACING_Y = 4;

  // Fixed -2 compensation + configurable additional shift
  const totalShift = MODULE_Q_SHIFT + moduleRowShift;

  for (let moduleRow = 0; moduleRow < rows; moduleRow++) {
    for (let moduleCol = 0; moduleCol < columns; moduleCol++) {

      for (const key of moduleKeys) {
        // Base hex coordinates: fixed compensation + additional shift
        const baseQ = moduleCol * spacingQ + key.hex.q + moduleRow * totalShift;
        const baseR = moduleRow * MODULE_SPACING_R + key.hex.r;
        // Configurable stagger applied on top
        const globalQ = baseQ + baseR * stagger.q;
        const globalR = baseR + baseQ * stagger.r;

        // Layout: 1 q-unit = 2 layout x-units; additional shift only (fixed -2 is already in module layout)
        const globalX = moduleCol * LAYOUT_SPACING_X + key.layout.x + moduleRow * moduleRowShift * 2;
        const globalY = moduleRow * LAYOUT_SPACING_Y + key.layout.y;

        allCoords.push({
          hex: { q: globalQ, r: globalR },
          layout: { x: globalX, y: globalY },
        });
      }
    }
  }

  return allCoords;
}
